import React, { useState, useEffect, useCallback } from 'react';
import liff from '@line/liff';
import { buildApiUrl } from '../../utils/apiBase';
import { supabase } from '../../supabaseClient';
import ExpenseModal from './ExpenseModal';
import ExpenseSummary from './ExpenseSummary';
import FriendSearchModal from '../users/FriendSearchModal';
import { FRIENDSHIP_STATUS, DEBT_STATUS } from '../../constants';
import ExpenseListTable from './ExpenseListTable';
import ExpenseListGrid from './ExpenseListGrid';

const ExpenseForm = ({ userProfile, appUser }) => {
  const METHOD_FILTER_UNSPECIFIED = '__unspecified__';
  const [mode, setMode] = useState('list'); // 'list' | 'summary' | 'clearing'
  
  // List State
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateError, setDateError] = useState(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [tempYear, setTempYear] = useState(null);
  const [tempMonth, setTempMonth] = useState(null);
  const [filterState, setFilterState] = useState({ payer: 'all', method: 'all', methodId: null });
  const [includeTripExpenses, setIncludeTripExpenses] = useState(false);
  
  // Date Range State (Default to current month)
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    start.setMinutes(start.getMinutes() - start.getTimezoneOffset());
    return start.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    end.setMinutes(end.getMinutes() - end.getTimezoneOffset());
    return end.toISOString().split('T')[0];
  });

  // Modal & Action State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFriendSearchOpen, setIsFriendSearchOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState(null); // { type: 'expense' | 'friend' | 'debt', id: string }
  const [editingExpense, setEditingExpense] = useState(null);
  const [toast, setToast] = useState(null); // { message: string, type: 'success' | 'error' }
  const [savedPayers, setSavedPayers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [listViewMode, setListViewMode] = useState('grid'); // 'table' | 'grid'
  const [debts, setDebts] = useState([]); // State for Debt Tracking
  const [friends, setFriends] = useState([]); // Friends list
  const [unpaidCount, setUnpaidCount] = useState(0); // Badge count

  const monthNamesTH = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedMode = params.get('expenseMode');
    const allowedModes = new Set(['list', 'summary', 'clearing']);
    if (!requestedMode || !allowedModes.has(requestedMode)) return;

    setMode(requestedMode);
    params.delete('expenseMode');
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', next);
  }, []);
  
  // Helper function to display payer name in list
  const getPayerDisplay = (expense) => {
    if (expense.payer_user_id === appUser?.id) return 'ฉัน';
    if (expense.identifier === appUser?.line_user_id || expense.identifier === userProfile?.userId) return 'ฉัน';
    const friendBySystemId = expense.payer_user_id
      ? friends.find(f => f.id === expense.payer_user_id)
      : null;
    if (friendBySystemId) return friendBySystemId.display_name;
    const friendByLineId = expense.identifier
      ? friends.find(f => f.line_user_id === expense.identifier)
      : null;
    if (friendByLineId) return friendByLineId.display_name;
    return expense.identifier?.length > 20 ? 'User ' + expense.identifier.substring(0, 4) : expense.identifier;
  };

  // Helper function to get payment style
  const getPaymentStyle = (methodKey) => {
    const normalizedMethodKey = typeof methodKey === 'string' ? methodKey.trim() : '';
    if (methodKey == null || !normalizedMethodKey) {
      return {
        color: 'var(--color-text-subtle)',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px', verticalAlign: 'text-bottom' }}>
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 8v5"></path>
            <circle cx="12" cy="16" r="1"></circle>
          </svg>
        ),
      };
    }

    const method = paymentMethods.find(pm =>
      String(pm.id) === String(methodKey) || pm.name === methodKey
    );
    const name = method?.name || (typeof methodKey === 'string' ? methodKey : null);
    const isCredit = method
      ? method.type === 'credit_card' || (!method.type && method.name !== 'เงินสด')
      : (name && name !== 'เงินสด');
    
    if (isCredit) {
        return {
            color: 'var(--color-accent)',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px', verticalAlign: 'text-bottom' }}>
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
            )
        };
    }
    return {
        color: 'var(--color-success)',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px', verticalAlign: 'text-bottom' }}>
                <rect x="2" y="6" width="20" height="12" rx="2"></rect>
                <circle cx="12" cy="12" r="2"></circle>
                <path d="M6 12h.01M18 12h.01"></path>
            </svg>
        )
    };
  };

  const fetchPayers = useCallback(async () => {
    if (!appUser) return;
    try {
      const { data, error } = await supabase
        .from('payers')
        .select('*')
        .eq('user_id', appUser.id) // ใช้ System ID แทน LINE ID
        .order('name', { ascending: true });
      if (error) throw error;
      setSavedPayers(data || []);
    } catch (error) {
      console.error('Error fetching payers:', error.message);
    }
  }, [appUser]);

  const fetchPaymentMethods = useCallback(async () => {
    // ดึงบัตรของฉัน + บัตรระบบ + บัตรที่แชร์ให้ฉัน (เฉพาะ expense ปกติ)
    try {
      const ownerId = appUser?.id;
      const ownerIdStr = ownerId != null ? String(ownerId) : null;
      const legacyLineId = userProfile?.userId;

      const baseOr = ['and(owner_id.is.null,user_id.is.null)'];
      if (ownerId != null) baseOr.push(`owner_id.eq.${ownerId}`);
      if (ownerIdStr) baseOr.push(`user_id.eq.${ownerIdStr}`);
      if (legacyLineId) baseOr.push(`user_id.eq.${legacyLineId}`);

      const { data: baseMethods, error: baseError } = await supabase
        .from('payment_methods')
        .select('*')
        .or(baseOr.join(','))
        .order('id', { ascending: true });
      if (baseError) throw baseError;

      let sharedMethods = [];
      let sharedIds = new Set();
      let shareMap = new Map();
      if (ownerId != null) {
        const accessToken = liff?.getAccessToken?.();
        if (accessToken) {
          const res = await fetch(buildApiUrl('/api/payment-method-shares-map'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken,
              ownerMethodIds: baseMethods.map(pm => pm.id)
            })
          });
          if (res.ok) {
            const json = await res.json();
            const sharedMethodIds = (json.sharedMethodIds || []).map(String);
            sharedIds = new Set(sharedMethodIds);
            const shareMapObj = json.shareMap || {};
            shareMap = new Map(Object.entries(shareMapObj));
            if (sharedMethodIds.length > 0) {
              const { data: sharedData, error: sharedFetchError } = await supabase
                .from('payment_methods')
                .select('*')
                .in('id', sharedMethodIds);
              if (sharedFetchError) throw sharedFetchError;
              sharedMethods = sharedData || [];
            }
          }
        }
      }

      const methodsMap = new Map();
      const flagsMap = new Map();
      const upsert = (pm, flags = {}) => {
        if (!pm || !pm.id) return;
        const existing = methodsMap.get(pm.id) || pm;
        methodsMap.set(pm.id, existing);
        flagsMap.set(pm.id, { ...(flagsMap.get(pm.id) || {}), ...flags });
      };

      (baseMethods || []).forEach(pm => {
        const isOwner = ownerId != null
          ? pm.owner_id === ownerId || (pm.user_id && (pm.user_id === legacyLineId || pm.user_id === ownerIdStr))
          : pm.user_id === legacyLineId;
        const isSystem = !pm.owner_id && !pm.user_id;
        upsert(pm, { isOwner, isSystem });
      });

      sharedMethods.forEach(pm => upsert(pm, { isShared: true }));

      const normalizedShareMap = new Map();
      shareMap.forEach((ids, key) => {
        normalizedShareMap.set(String(key), (ids || []).map(String));
      });

      const friendIdToName = new Map(friends.map(f => [f.id, f.display_name]));
      const friendLineToName = new Map(friends.map(f => [f.line_user_id, f.display_name]));

      const merged = Array.from(methodsMap.values()).map(pm => {
        const flags = flagsMap.get(pm.id) || {};
        const access = flags.isSystem
          ? 'system'
          : flags.isOwner
            ? 'owner'
            : flags.isShared || sharedIds.has(pm.id)
              ? 'shared'
              : 'shared';
        const ownerName = pm.owner_id
          ? friendIdToName.get(pm.owner_id)
          : pm.user_id
            ? friendLineToName.get(pm.user_id)
            : null;
        return {
          ...pm,
          access,
          owner_name: ownerName,
          shared_to_ids: normalizedShareMap.get(String(pm.id)) || []
        };
      });

      setPaymentMethods(merged);
    } catch (error) {
      console.error('Error fetching payment methods:', error.message);
    }
  }, [friends, userProfile, appUser]);

  const fetchFriends = useCallback(async () => {
      if (!appUser) return;
      try {
          // ดึงข้อมูลเพื่อนที่ user_id_1 หรือ user_id_2 เป็นเรา
          const { data, error } = await supabase
              .from('friendships')
              .select(`
                  user_id_1,
                  user_id_2,
                  user1:users!user_id_1(id, display_name, picture_url, username, line_user_id),
                  user2:users!user_id_2(id, display_name, picture_url, username, line_user_id)
              `)
              .or(`user_id_1.eq.${appUser.id},user_id_2.eq.${appUser.id}`)
              .eq('status', FRIENDSHIP_STATUS.ACCEPTED); // เลือกเฉพาะที่เป็นเพื่อนกันแล้ว

          if (error) throw error;

          // แปลงข้อมูลให้อยู่ในรูปแบบ List ของเพื่อน
          const friendList = data.map(f => {
              return f.user_id_1 === appUser.id ? f.user2 : f.user1;
          });
          
          setFriends(friendList);
      } catch (error) {
          console.error('Error fetching friends:', error.message);
      }
  }, [appUser]);

  const fetchDebts = useCallback(async () => {
      setLoading(true);
      try {
          let query = supabase
              .from('debts')
              .select('*') // เอา creator ออกก่อนเพื่อความชัวร์ (ป้องกัน FK Error)
              .order('created_at', { ascending: false });

          const orConditions = [`user_id.eq.${userProfile?.userId}`];
          if (appUser?.id) {
              orConditions.push(`counterparty_id.eq.${appUser.id}`);
          }
          
          const { data, error } = await query.or(orConditions.join(','));

          if (error) throw error;
          
          const allDebts = data || [];
          setDebts(allDebts);

          // Calculate Unpaid Count
          const count = allDebts.filter(d => d.status === DEBT_STATUS.UNPAID).length;
          setUnpaidCount(count);

      } catch (error) {
          console.error('Error fetching debts:', error.message);
      } finally {
          setLoading(false);
      }
  }, [appUser, userProfile]);

  const fetchExpenses = useCallback(async () => {
    if (!userProfile?.userId) return;
    setLoading(true);
    try {
      const accessToken = liff?.getAccessToken?.();
      if (!accessToken) throw new Error('ไม่พบ LINE access token');

      let startIso = null;
      let endIso = null;
      if (startDate && endDate) {
        const [sY, sM, sD] = startDate.split('-').map(Number);
        const startObj = new Date(sY, sM - 1, sD);
        const [eY, eM, eD] = endDate.split('-').map(Number);
        const endObj = new Date(eY, eM - 1, eD, 23, 59, 59, 999);
        startIso = startObj.toISOString();
        endIso = endObj.toISOString();
      }

      const res = await fetch(buildApiUrl('/api/expenses'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          action: 'list',
          filters: {
            startIso,
            endIso,
            searchTerm,
            payerFilter: filterState.payer,
            paymentMethodId: filterState.methodId,
            paymentMethod: filterState.method !== 'all' ? filterState.method : null,
            includeTrip: includeTripExpenses
          }
        })
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'โหลดรายจ่ายไม่สำเร็จ');
      }

      const json = await res.json();
      setExpenses(json.expenses || []);
    } catch (error) {
      console.error('Error fetching expenses:', error.message);
    } finally {
      setLoading(false);
    }
  }, [userProfile, startDate, endDate, searchTerm, filterState, includeTripExpenses]);

  const formatDateOnly = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const setMonthRange = useCallback((year, monthIndex) => {
    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 0);
    setStartDate(formatDateOnly(start));
    setEndDate(formatDateOnly(end));
  }, []);

  const getActiveMonthDate = () => {
    if (startDate) {
      const [y, m] = startDate.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date();
  };

  useEffect(() => {
    if (!startDate || !endDate) {
      const now = new Date();
      setMonthRange(now.getFullYear(), now.getMonth());
    }
  }, [startDate, endDate, setMonthRange]);

  useEffect(() => {
    if (appUser) {
        fetchFriends();
    }
  }, [appUser, fetchFriends]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  useEffect(() => {
    if (mode === 'list' || mode === 'summary') {
      fetchExpenses();
    }
    fetchPayers();
    fetchDebts(); // Fetch debts always to update badge count
  }, [mode, fetchExpenses, fetchPayers, fetchDebts]);

  const handleEdit = (item) => {
    setEditingExpense(item);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteConfig({ type: 'expense', id });
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteConfig) return;
    setLoading(true);
    try {
        if (deleteConfig.type === 'expense') {
            const accessToken = liff?.getAccessToken?.();
            if (!accessToken) throw new Error('No LINE access token');
            const delRes = await fetch(buildApiUrl('/api/expenses'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ accessToken, action: 'delete', expenseId: deleteConfig.id }),
            });
            if (!delRes.ok) {
              const j = await delRes.json().catch(() => ({}));
              throw new Error(j.error || `API error ${delRes.status}`);
            }
            showToastMessage('ลบรายการเรียบร้อยแล้ว');
            fetchExpenses();
        } else if (deleteConfig.type === 'friend') {
            const { error } = await supabase
              .from('friendships')
              .delete()
              .or(`and(user_id_1.eq.${appUser.id},user_id_2.eq.${deleteConfig.id}),and(user_id_1.eq.${deleteConfig.id},user_id_2.eq.${appUser.id})`);
            if (error) throw error;
            showToastMessage('ลบเพื่อนเรียบร้อยแล้ว');
            fetchFriends();
        } else if (deleteConfig.type === 'debt') {
            const { error } = await supabase.from('debts').delete().eq('id', deleteConfig.id);
            if (error) throw error;
            fetchDebts();
            showToastMessage('ลบรายการแล้ว');
        }
    } catch (error) {
        console.error('Error deleting:', error.message);
        showToastMessage('เกิดข้อผิดพลาดในการลบ: ' + error.message, 'error');
    } finally {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setDeleteConfig(null);
    }
  };

  const handleAddFriend = async (targetUsername) => {
      if (!appUser) return;
      try {
          // 1. Find user by username
          const { data: targetUser, error: searchError } = await supabase
              .from('users')
              .select('id, display_name')
              .eq('username', targetUsername)
              .single();
          
          if (searchError || !targetUser) throw new Error('ไม่พบผู้ใช้งานนี้');
          if (targetUser.id === appUser.id) throw new Error('ไม่สามารถเพิ่มตัวเองเป็นเพื่อนได้');

          // 2. Add friendship
          const { error: addError } = await supabase.from('friendships').insert({
              user_id_1: appUser.id,
              user_id_2: targetUser.id
          });

          if (addError) throw addError;
          showToastMessage(`เพิ่ม ${targetUser.display_name} เป็นเพื่อนแล้ว`);
          fetchFriends();
      } catch (error) {
          showToastMessage(error.message, 'error');
          throw error; // Rethrow to let Modal know
      }
  };

  const handleDeleteFriend = async (friendId) => {
      if (!appUser) return;
      setDeleteConfig({ type: 'friend', id: friendId });
      setIsDeleteModalOpen(true);
  };

  const searchUsers = async (keyword) => {
    if (!appUser || !keyword || keyword.length < 3) return [];
    try {
      // Get blocked users
      const { data: blocked } = await supabase
        .from('blocked_users')
        .select('blocker_id, blocked_id')
        .or(`blocker_id.eq.${appUser.id},blocked_id.eq.${appUser.id}`);
      const blockedIds = blocked ? blocked.map(b => b.blocker_id === appUser.id ? b.blocked_id : b.blocker_id) : [];

      // Get reported users
      const { data: reported } = await supabase
        .from('spam_reports')
        .select('reported_id')
        .eq('reporter_id', appUser.id);
      const reportedIds = reported ? reported.map(r => r.reported_id) : [];

      const { data, error } = await supabase
        .from('users')
        .select('id, username, display_name, picture_url')
        .ilike('username', `%${keyword}%`)
        .neq('id', appUser.id) // ไม่ค้นหาตัวเอง
        .limit(5); // จำกัดจำนวนผลลัพธ์

      if (error) throw error;
      
      // กรองคนที่เป็นเพื่อนอยู่แล้วออก
      const currentFriendIds = [...friends.map(f => f.id), ...blockedIds, ...reportedIds];
      return data.filter(u => !currentFriendIds.includes(u.id));
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  };

  const handleSaveExpense = async (formData) => {
    setLoading(true);
    try {
      const buildMonthlyOccurrences = (baseDate, months) => {
        const dates = [];
        for (let i = 0; i < months; i += 1) {
          const next = new Date(baseDate);
          next.setMonth(baseDate.getMonth() + i);
          dates.push(next);
        }
        return dates;
      };

      // Determine identifier and payer_user_id
      let identifier = formData.identifier;
      let payerUserId = null;

      if (formData.payerMode === 'other') {
          if (formData.selectedFriend) {
              // Case: Selected a Friend (System User)
              identifier = formData.selectedFriend.line_user_id || formData.selectedFriend.display_name;
              payerUserId = formData.selectedFriend.id;
          } else {
              // Case: Manual Input / Saved Payer (Local)
              identifier = formData.otherPayerName;
              // Check if new local payer, save to payers table
              const isExisting = savedPayers.some(p => p.name.toLowerCase() === identifier.toLowerCase());
              if (!isExisting && appUser) {
                   await supabase.from('payers').insert([{ 
                       name: identifier,
                       user_id: appUser.id // ใช้ System ID แทน LINE ID
                   }]);
                   fetchPayers();
              }
          }
      } else {
          // Case: Me
          identifier = appUser?.line_user_id || userProfile?.userId;
          payerUserId = appUser?.id;
      }

      const payload = { 
        user_id: userProfile?.userId, // บันทึกว่าใครเป็นเจ้าของรายการ
        identifier: identifier,
        payer_user_id: payerUserId, // Link to system user if available
        amount: parseFloat(formData.amount),
        note: formData.note,
        currency: formData.currency,
        occurred_at: new Date(formData.occurredAt).toISOString(),
        payment_method_id: formData.paymentMethodId || null,
        payment_method: formData.paymentMethod
      };

      const accessToken = liff?.getAccessToken?.();
      if (!accessToken) throw new Error('No LINE access token');

      if (editingExpense) {
        const res = await fetch(buildApiUrl('/api/expenses'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken, action: 'update', expenseId: editingExpense.id, payload }),
        });
        if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `API error ${res.status}`); }
        showToastMessage('แก้ไขรายการเรียบร้อยแล้ว');
      } else {
        const shouldRepeat = formData.repeatType === 'monthly' && formData.repeatMonths > 1;
        const rows = shouldRepeat
          ? buildMonthlyOccurrences(new Date(formData.occurredAt), formData.repeatMonths).map((date) => ({
              ...payload,
              occurred_at: date.toISOString()
            }))
          : [payload];

        await Promise.all(rows.map((row) =>
          fetch(buildApiUrl('/api/expenses'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken, action: 'create', payload: row }),
          }).then(async (res) => {
            if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `API error ${res.status}`); }
          })
        ));
        showToastMessage('บันทึกรายจ่ายเรียบร้อยแล้ว');
      }
      
      setIsModalOpen(false);
      setEditingExpense(null);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error.message);
      showToastMessage('เกิดข้อผิดพลาดในการบันทึก: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToastMessage = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  const handleSaveDebts = async (othersData, summaryState) => {
      if (!othersData || othersData.length === 0) {
          showToastMessage('ไม่มีรายการหนี้ให้บันทึก', 'error');
          return;
      }
      setLoading(true);
      try {
          // Process debts in parallel
          await Promise.all(othersData.map(async (item) => {
              // item.net > 0 : ฉันได้คืน (เขาติดฉัน) -> Debtor: item.name, Creditor: 'ฉัน'
              // item.net < 0 : ฉันต้องจ่าย (ฉันติดเขา) -> Debtor: 'ฉัน', Creditor: item.name
              const isReceiving = item.net >= 0;
              const debtor = isReceiving ? item.name : 'ฉัน';
              const creditor = isReceiving ? 'ฉัน' : item.name;
              const amount = Math.abs(item.net);

              // Find friend to link counterparty_id
              let friend = friends.find(f => f.display_name === item.name);
              if (!friend) {
                  friend = friends.find(f => f.display_name.toLowerCase() === item.name.toLowerCase());
              }
              const counterpartyId = friend ? friend.id : null;
              let currentDebtId;

              // 1. Check existing unpaid debt for this pair
              const { data: existingDebt, error: fetchError } = await supabase
                  .from('debts')
                  .select('id')
                  .eq('user_id', userProfile?.userId)
                  .eq('debtor', debtor)
                  .eq('creditor', creditor)
                  .eq('status', DEBT_STATUS.UNPAID)
                  .order('created_at', { ascending: false })
                  .limit(1)
                  .maybeSingle();

              if (fetchError) throw fetchError;

              if (existingDebt) {
                  // 2. Update existing
                  const { error } = await supabase.from('debts').update({
                      amount: amount,
                      note: `ยอดเคลียร์ (อัปเดต) ณ วันที่ ${new Date().toLocaleDateString('th-TH')}`
                  }).eq('id', existingDebt.id);
                  if (error) throw error;
                  currentDebtId = existingDebt.id;
              } else {
                  // 3. Insert new
                  const { data: newDebt, error } = await supabase.from('debts').insert({
                      user_id: userProfile?.userId,
                      debtor,
                      creditor,
                      amount,
                      status: DEBT_STATUS.UNPAID,
                      note: `ยอดเคลียร์ ณ วันที่ ${new Date().toLocaleDateString('th-TH')}`,
                      counterparty_id: counterpartyId
                  }).select();
                  if (error) throw error;
                  currentDebtId = newDebt[0].id;
              }

              // Update expenses with debt_id
              const expenseIds = item.details.map(d => d.expenseId).filter(id => id);
              if (expenseIds.length > 0) {
                  const { error: updateError } = await supabase
                      .from('expenses')
                      .update({ 
                          is_cleared: true,
                          debt_id: currentDebtId,
                          // เราอัปเดต split_mode ทีหลังใน loop ข้างล่าง หรือรวมตรงนี้ก็ได้ แต่แยกไว้เพื่อความชัวร์เรื่อง state
                      })
                      .in('id', expenseIds);
                  if (updateError) throw updateError;
              }
          }));

          // Update expenses to mark as cleared and save state
          const { splitModes, billAssignments } = summaryState;
          // Update ALL involved expenses (even if already cleared) to sync latest state
          const updates = expenses
            .filter(e => splitModes[e.id] && splitModes[e.id] !== 'none') 
            .map(e => ({
                id: e.id,
                is_cleared: true,
                split_mode: splitModes[e.id],
                assigned_to: billAssignments[e.id] || null
            }));

          if (updates.length > 0) {
              await Promise.all(updates.map(update => 
                  supabase.from('expenses').update(update).eq('id', update.id)
              ));
          }

          showToastMessage('บันทึกรายการหนี้เรียบร้อยแล้ว');
          setMode('clearing'); // ไปหน้าเคลียร์บิลเพื่อดูผลลัพธ์
      } catch (error) {
          console.error('Error saving debts:', error);
          showToastMessage('บันทึกไม่สำเร็จ: ' + error.message, 'error');
      } finally {
          setLoading(false);
      }
  };

  const visibleExpenses = includeTripExpenses
    ? expenses
    : expenses.filter((item) => !item.trip_id);

  return (
    <div style={{ padding: '20px', paddingBottom: '100px', backgroundColor: 'var(--color-bg)', minHeight: '100vh', fontFamily: 'var(--font-body)' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {mode === 'summary' ? (
          <ExpenseSummary 
            expenses={expenses}
            savedPayers={savedPayers}
            userProfile={userProfile}
            onBack={() => setMode('list')}
            onShowToast={showToastMessage}
            onSaveDebts={handleSaveDebts}
            appUser={appUser}
            friends={friends}
            paymentMethods={paymentMethods}
          />
        ) : mode === 'clearing' ? (
            <DebtClearanceList 
                debts={debts} 
                onBack={() => setMode('list')} 
                onRefresh={fetchDebts}
                onShowToast={showToastMessage}
                loading={loading}
                userProfile={userProfile}
                appUser={appUser}
                onDelete={(id) => {
                    setDeleteConfig({ type: 'debt', id });
                    setIsDeleteModalOpen(true);
                }}
            />
        ) : (
          <>
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={() => {
                                const base = getActiveMonthDate();
                                setTempYear(base.getFullYear());
                                setTempMonth(base.getMonth());
                                setIsMonthPickerOpen(true);
                            }}
                            style={{
                                padding: '10px 12px',
                                borderRadius: '10px',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-glass-strong)',
                                color: 'var(--color-text)',
                                fontWeight: '600',
                                cursor: 'pointer',
                                minWidth: '160px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px'
                            }}
                        >
                            <span>{monthNamesTH[getActiveMonthDate().getMonth()]} {getActiveMonthDate().getFullYear()}</span>
                            <span style={{ color: 'var(--color-text-subtle)', fontSize: '12px' }}>▼</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsDateRangeOpen(true)}
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-glass-strong)',
                                color: 'var(--color-text)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 6px 14px rgba(30, 70, 90, 0.1)'
                            }}
                            title="ค้นหาตามช่วงวันที่"
                            aria-label="ค้นหาตามช่วงวันที่"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        </button>
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 150px))', gap: '14px', width: 'fit-content', maxWidth: '100%' }}>
                    <button 
                        onClick={() => setMode('summary')}
                        style={{
                            border: '1px solid rgba(var(--color-primary-rgb), 0.18)',
                            background: 'rgba(255, 255, 255, 0.9)',
                            color: 'var(--color-text)',
                            borderRadius: '18px',
                            padding: '14px 12px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            minHeight: '98px',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
                        }}
                    >
                        <span style={{ width: '36px', height: '36px', borderRadius: '14px', background: 'var(--color-primary-soft)', color: 'var(--color-primary)', display: 'grid', placeItems: 'center' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="16" rx="2"></rect>
                                <line x1="7" y1="8" x2="17" y2="8"></line>
                                <line x1="7" y1="12" x2="13" y2="12"></line>
                                <line x1="7" y1="16" x2="15" y2="16"></line>
                            </svg>
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-primary)', fontFamily: 'var(--font-display)' }}>สรุปรายจ่าย</span>
                    </button>
                    <button 
                        onClick={() => setMode('clearing')}
                        style={{
                            position: 'relative',
                            border: '1px solid rgba(var(--color-primary-rgb), 0.18)',
                            background: 'rgba(255, 255, 255, 0.9)',
                            color: 'var(--color-text)',
                            borderRadius: '18px',
                            padding: '14px 12px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            minHeight: '98px',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
                        }}
                    >
                        <span style={{ width: '36px', height: '36px', borderRadius: '14px', background: 'var(--color-primary-soft)', color: 'var(--color-primary)', display: 'grid', placeItems: 'center' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12a9 9 0 1 1-3.1-6.7"></path>
                                <polyline points="22 4 21 12 13 11"></polyline>
                            </svg>
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-primary)', fontFamily: 'var(--font-display)' }}>เคลียร์ยอดจ่าย</span>
                        {unpaidCount > 0 && (
                            <span style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: 'var(--color-danger)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                border: '2px solid #fff'
                            }}>
                                {unpaidCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Payment Method Filter Chips */}
            <div style={{ marginBottom: '15px' }}>
                <style>{`
                    ::-webkit-scrollbar { display: none; }
                `}</style>
                
                {/* Row 1: Payer Category */}
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '5px', marginBottom: filterState.payer !== 'all' ? '8px' : '0', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    <button
                        onClick={() => setFilterState({ payer: 'all', method: 'all', methodId: null })}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: filterState.payer === 'all' ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                            backgroundColor: filterState.payer === 'all' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                            color: filterState.payer === 'all' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: filterState.payer === 'all' ? '600' : '400',
                            transition: 'all 0.2s'
                        }}
                    >
                        ทั้งหมด
                    </button>
                    <button
                    onClick={() => setFilterState({ payer: 'me', method: 'all', methodId: null })}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: filterState.payer === 'me' ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                        backgroundColor: filterState.payer === 'me' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                        color: filterState.payer === 'me' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: filterState.payer === 'me' ? '600' : '400',
                        transition: 'all 0.2s'
                    }}
                >
                    ที่ฉันจ่าย
                </button>
                <button
                    onClick={() => setFilterState({ payer: 'others', method: 'all', methodId: null })}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: filterState.payer === 'others' ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                        backgroundColor: filterState.payer === 'others' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                        color: filterState.payer === 'others' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: filterState.payer === 'others' ? '600' : '400',
                        transition: 'all 0.2s'
                    }}
                >
                    ที่คนอื่นจ่าย
                </button>
                </div>

                {/* Row 2: Payment Methods (Only if Payer is selected) */}
                {filterState.payer !== 'all' && (
                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '5px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        <button
                            onClick={() => setFilterState(prev => ({ ...prev, method: 'all', methodId: null }))}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: filterState.method === 'all' ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                backgroundColor: filterState.method === 'all' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                color: filterState.method === 'all' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: filterState.method === 'all' ? '600' : '400',
                                transition: 'all 0.2s'
                            }}
                        >
                            ทุกช่องทาง
                        </button>
                        <button
                            onClick={() => setFilterState(prev => ({ ...prev, method: 'เงินสด', methodId: null }))}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: filterState.method === 'เงินสด' ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                backgroundColor: filterState.method === 'เงินสด' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                color: filterState.method === 'เงินสด' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: filterState.method === 'เงินสด' ? '600' : '400',
                                transition: 'all 0.2s'
                            }}
                        >
                            เงินสด
                        </button>
                        <button
                            onClick={() => setFilterState(prev => ({ ...prev, method: METHOD_FILTER_UNSPECIFIED, methodId: null }))}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: filterState.method === METHOD_FILTER_UNSPECIFIED ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                backgroundColor: filterState.method === METHOD_FILTER_UNSPECIFIED ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                color: filterState.method === METHOD_FILTER_UNSPECIFIED ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: filterState.method === METHOD_FILTER_UNSPECIFIED ? '600' : '400',
                                transition: 'all 0.2s'
                            }}
                        >
                            ยังไม่ได้ระบุ
                        </button>
                        
                        {filterState.method !== 'เงินสด' && paymentMethods
                            .filter(pm => {
                                if (pm.name === 'เงินสด') return false;
                                const isMine = pm.owner_id === appUser?.id
                                  || (pm.user_id && (pm.user_id === userProfile?.userId || pm.user_id === String(appUser?.id)));
                                const isSystem = !pm.owner_id && !pm.user_id;
                                if (isSystem) return false;
                                if (filterState.payer === 'me') {
                                    return isMine;
                                }
                                return !isMine;
                            })
                            .map(pm => (
                                <button
                                    key={pm.id}
                                    onClick={() => setFilterState(prev => ({ ...prev, method: pm.name, methodId: pm.id }))}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        border: filterState.method === pm.name ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                        backgroundColor: filterState.method === pm.name ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                        color: filterState.method === pm.name ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: filterState.method === pm.name ? '600' : '400',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {pm.name}
                                </button>
                            ))
                        }
                    </div>
                )}
            </div>

            {/* Search Bar */}
            <div style={{ marginBottom: '15px' }}>
                    <input
                    type="text"
                    placeholder="🔍 ค้นหาจากบันทึก หรือ จำนวนเงิน..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid var(--color-border)',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                        background: 'var(--color-glass-strong)',
                        color: 'var(--color-text)',
                        boxShadow: '0 8px 16px rgba(30, 70, 90, 0.08)',
                        outlineColor: 'var(--color-primary)'
                    }}
                    />
            </div>

            {/* Total & View Mode Toggle */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                        รวม: {visibleExpenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toLocaleString()}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            onClick={() => setListViewMode('grid')}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: listViewMode === 'grid' ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                backgroundColor: listViewMode === 'grid' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                color: listViewMode === 'grid' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '6px',
                                fontSize: '14px', fontWeight: '500'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                            Grid
                        </button>
                        <button 
                            onClick={() => setListViewMode('table')}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: listViewMode === 'table' ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                backgroundColor: listViewMode === 'table' ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                color: listViewMode === 'table' ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '6px',
                                fontSize: '14px', fontWeight: '500'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                            Table
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '999px', border: '1px solid var(--color-border)', background: 'var(--color-glass-strong)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>แสดงค่าใช้จ่ายในทริป</span>
                        <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={includeTripExpenses}
                                onChange={(e) => setIncludeTripExpenses(e.target.checked)}
                                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                            />
                            <span
                                style={{
                                    width: '38px',
                                    height: '20px',
                                    backgroundColor: includeTripExpenses ? 'var(--color-primary)' : 'var(--color-border)',
                                    borderRadius: '999px',
                                    position: 'relative',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '2px',
                                        left: includeTripExpenses ? '20px' : '2px',
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: 'white',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                        transition: 'all 0.2s'
                                    }}
                                />
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* List */}
            {loading ? (
                    <p style={{ textAlign: 'center' }}>กำลังโหลด...</p>
                ) : visibleExpenses.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>ไม่พบรายจ่ายในเดือนนี้</p>
                ) : (
                    <>
                        {listViewMode === 'table' ? (
                            <ExpenseListTable 
                                expenses={visibleExpenses} 
                                onEdit={handleEdit} 
                                onDelete={handleDeleteClick} 
                                userProfile={userProfile} 
                                getPayerDisplay={getPayerDisplay} 
                                getPaymentStyle={getPaymentStyle} 
                            />
                        ) : (
                            <ExpenseListGrid 
                                expenses={visibleExpenses} 
                                onEdit={handleEdit} 
                                onDelete={handleDeleteClick} 
                                userProfile={userProfile} 
                                getPayerDisplay={getPayerDisplay} 
                                getPaymentStyle={getPaymentStyle} 
                            />
                        )}
                    </>
            )}
          </>
        )}
      </div>

      {/* Sticky Add Button (Only in list mode) */}
      {mode === 'list' && (
          <div style={{
              position: 'fixed',
              bottom: '30px',
              left: '0',
              right: '0',
              display: 'flex',
              justifyContent: 'center',
              zIndex: 100,
              pointerEvents: 'none' // wrapper shouldn't block clicks
          }}>
              <button
                  onClick={() => {
                      setEditingExpense(null);
                      setIsModalOpen(true);
                  }}
                  style={{
                      pointerEvents: 'auto',
                      width: '90%',
                      maxWidth: '400px',
                      padding: '16px',
                      backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      boxShadow: '0 16px 30px rgba(var(--color-primary-rgb), 0.35)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                  }}
              >
                  + เพิ่มรายจ่าย
              </button>
          </div>
      )}

      {isDateRangeOpen && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1200, display: 'flex', alignItems: 'flex-end' }} onClick={() => setIsDateRangeOpen(false)}>
              <div style={{ width: '100%', backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '20px', height: '90vh', overflowY: 'auto', animation: 'slideUp 0.3s ease-out' }} onClick={(e) => e.stopPropagation()}>
                  <style>
                      {`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}
                  </style>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--color-text)' }}>ค้นหาตามช่วงวันที่</h3>
                      <button onClick={() => setIsDateRangeOpen(false)} style={{ border: 'none', background: 'none', fontSize: '24px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>&times;</button>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', marginBottom: '5px', color: 'var(--color-text-muted)', fontSize: '14px' }}>วันที่เริ่มต้น</label>
                          <input 
                              type="date" 
                              value={startDate} 
                              onClick={(e) => e.target.showPicker && e.target.showPicker()}
                              onChange={(e) => {
                                  const newDate = e.target.value;
                                  setDateError(null);
                                  if (endDate && newDate > endDate) {
                                      setDateError("วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด");
                                      return;
                                  }
                                  setStartDate(newDate);
                              }}
                              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-glass-strong)', color: 'var(--color-text)', fontSize: '16px', boxSizing: 'border-box', cursor: 'pointer' }}
                          />
                      </div>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', marginBottom: '5px', color: 'var(--color-text-muted)', fontSize: '14px' }}>วันที่สิ้นสุด</label>
                          <input 
                              type="date" 
                              value={endDate} 
                              onClick={(e) => e.target.showPicker && e.target.showPicker()}
                              onChange={(e) => {
                                  const newDate = e.target.value;
                                  setDateError(null);
                                  if (startDate && newDate < startDate) {
                                      setDateError("วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มต้น");
                                      return;
                                  }
                                  setEndDate(newDate);
                              }}
                              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-glass-strong)', color: 'var(--color-text)', fontSize: '16px', boxSizing: 'border-box', cursor: 'pointer' }}
                          />
                      </div>
                  </div>

                  {dateError && <div style={{ color: 'var(--color-danger)', marginTop: '12px', fontSize: '14px', backgroundColor: 'var(--color-danger-soft)', padding: '8px 12px', borderRadius: '6px' }}>{dateError}</div>}

                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                      <button
                          type="button"
                          onClick={() => {
                              setDateError(null);
                              const now = new Date();
                              const start = new Date(now.getFullYear(), now.getMonth(), 1);
                              const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                              start.setMinutes(start.getMinutes() - start.getTimezoneOffset());
                              end.setMinutes(end.getMinutes() - end.getTimezoneOffset());
                              setStartDate(start.toISOString().split('T')[0]);
                              setEndDate(end.toISOString().split('T')[0]);
                          }}
                          style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '10px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-glass-strong)',
                              color: 'var(--color-text)',
                              cursor: 'pointer',
                              fontWeight: '600'
                          }}
                      >
                          เคลียร์
                      </button>
                      <button
                          type="button"
                          onClick={() => {
                              if (startDate && endDate && startDate > endDate) {
                                  setDateError('วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด');
                                  return;
                              }
                              setDateError(null);
                              setIsDateRangeOpen(false);
                          }}
                          style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '10px',
                              border: 'none',
                              backgroundColor: 'var(--color-primary)',
                              backgroundImage: 'var(--color-primary-gradient)',
                              color: 'white',
                              cursor: 'pointer',
                              fontWeight: '700',
                              boxShadow: '0 6px 14px rgba(var(--color-primary-rgb), 0.25)'
                          }}
                      >
                          ค้นหา
                      </button>
                  </div>
              </div>
          </div>
      )}

      {isMonthPickerOpen && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1200, display: 'flex', alignItems: 'flex-end' }} onClick={() => setIsMonthPickerOpen(false)}>
              <div style={{ width: '100%', backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '20px', height: '90vh', overflowY: 'auto', animation: 'slideUp 0.3s ease-out' }} onClick={(e) => e.stopPropagation()}>
                  <style>
                      {`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}
                  </style>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--color-text)' }}>เลือกเดือน</h3>
                      <button onClick={() => setIsMonthPickerOpen(false)} style={{ border: 'none', background: 'none', fontSize: '24px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>&times;</button>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>ปี</div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {(() => {
                              const nowYear = new Date().getFullYear();
                              const yearOptions = Array.from(new Set([nowYear, nowYear - 1, tempYear].filter(Boolean)));
                              return yearOptions.map((year) => (
                                  <button
                                      key={year}
                                      type="button"
                                      onClick={() => setTempYear(year)}
                                      style={{
                                          padding: '8px 14px',
                                          borderRadius: '18px',
                                          border: tempYear === year ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                          backgroundColor: tempYear === year ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                          color: tempYear === year ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          fontWeight: tempYear === year ? '600' : '400'
                                      }}
                                  >
                                      {year}
                                  </button>
                              ));
                          })()}
                      </div>
                  </div>

                  <div>
                      <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>เดือน</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                          {monthNamesTH.map((name, idx) => (
                              <button
                                  key={name}
                                  type="button"
                                  onClick={() => setTempMonth(idx)}
                                  style={{
                                      padding: '10px 12px',
                                      borderRadius: '12px',
                                      border: tempMonth === idx ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                                      backgroundColor: tempMonth === idx ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                      color: tempMonth === idx ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: tempMonth === idx ? '600' : '400'
                                  }}
                              >
                                  {name}
                              </button>
                          ))}
                      </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                      <button
                          type="button"
                          onClick={() => setIsMonthPickerOpen(false)}
                          style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '10px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-glass-strong)',
                              color: 'var(--color-text)',
                              cursor: 'pointer',
                              fontWeight: '600'
                          }}
                      >
                          ยกเลิก
                      </button>
                      <button
                          type="button"
                          onClick={() => {
                              if (tempYear != null && tempMonth != null) {
                                  setMonthRange(tempYear, tempMonth);
                              }
                              setIsMonthPickerOpen(false);
                          }}
                          style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '10px',
                              border: 'none',
                              backgroundColor: 'var(--color-primary)',
                              backgroundImage: 'var(--color-primary-gradient)',
                              color: 'white',
                              cursor: 'pointer',
                              fontWeight: '700',
                              boxShadow: '0 6px 14px rgba(var(--color-primary-rgb), 0.25)'
                          }}
                      >
                          เลือก
                      </button>
                  </div>
              </div>
          </div>
      )}

      <ExpenseModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveExpense}
        editingExpense={editingExpense}
        userProfile={userProfile}
        savedPayers={savedPayers}
        paymentMethods={paymentMethods}
        onPaymentMethodsChange={fetchPaymentMethods}
        loading={loading}
        onShowToast={showToastMessage}
        friends={friends}
        shareCandidates={friends}
        onAddFriend={handleAddFriend}
        appUser={appUser}
        onDeleteFriend={handleDeleteFriend}
        onSearchUsers={searchUsers}
      />

      {isFriendSearchOpen && (
        <FriendSearchModal
            onClose={() => setIsFriendSearchOpen(false)}
            onSearchUsers={searchUsers}
            onAddFriend={handleAddFriend}
        />
      )}

      {isDeleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--color-glass-strong)', border: '1px solid rgba(255, 255, 255, 0.8)', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(30, 70, 90, 0.16)', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการลบ</h3>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '25px' }}>
                {deleteConfig?.type === 'friend' 
                    ? 'คุณต้องการลบเพื่อนคนนี้ใช่หรือไม่?' 
                    : 'คุณต้องการลบรายการนี้ใช่หรือไม่?'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                <button 
                    onClick={() => setIsDeleteModalOpen(false)}
                    style={{ padding: '10px 20px', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                    ยกเลิก
                </button>
                <button 
                    onClick={confirmDelete}
                    style={{ padding: '10px 20px', backgroundColor: 'var(--color-danger)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 6px 14px rgba(224, 86, 75, 0.35)' }}
                >
                    {loading ? 'กำลังลบ...' : 'ลบรายการ'}
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div style={{
            position: 'fixed',
            bottom: '30px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: toast.type === 'error' ? 'var(--color-danger)' : 'var(--color-text)',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 3000,
            fontSize: '14px',
            textAlign: 'center',
            minWidth: '200px',
            animation: 'fadeIn 0.3s ease-in-out'
        }}>
            {toast.message}
        </div>
      )}
    </div>
  );
};

// New Component: Debt Clearance List
const DebtClearanceList = ({ debts, onBack, onRefresh, onShowToast, loading, userProfile, appUser, onDelete }) => {
    const toggleStatus = async (debt) => {
        const newStatus = debt.status === DEBT_STATUS.PAID ? DEBT_STATUS.UNPAID : DEBT_STATUS.PAID;
        try {
            const { error } = await supabase
                .from('debts')
                .update({ status: newStatus })
                .eq('id', debt.id);
            if (error) throw error;
            onRefresh();
            onShowToast(newStatus === DEBT_STATUS.PAID ? 'เคลียร์ยอดแล้ว' : 'ยกเลิกการเคลียร์');
        } catch (error) {
            console.error('Error updating debt:', error);
            onShowToast('อัปเดตไม่สำเร็จ', 'error');
        }
    };

    return (
        <div>
            <button 
              onClick={onBack}
              style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', marginBottom: '15px' }}
            >
              &larr; กลับหน้ารายการ
            </button>
            <h2 style={{ color: 'var(--color-primary)', textAlign: 'center', marginBottom: '20px', fontFamily: 'var(--font-display)' }}>รายการเคลียร์บิล</h2>

            {loading ? <p style={{ textAlign: 'center' }}>กำลังโหลด...</p> : debts.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--color-text-subtle)' }}>ไม่มีรายการค้างเคลียร์</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {debts.map(item => {
                        // Determine display names based on who is viewing
                        const isCreator = item.user_id === userProfile?.userId;
                        const creatorName = item.creator?.display_name || 'เพื่อน';
                        
                        let displayDebtor = item.debtor;
                        let displayCreditor = item.creditor;

                        if (!isCreator) {
                            // If I am not the creator, 'ฉัน' in DB means the Creator
                            if (displayDebtor === 'ฉัน') displayDebtor = creatorName;
                            if (displayCreditor === 'ฉัน') displayCreditor = creatorName;

                            // If I am the counterparty, map my name to 'ฉัน'
                            // Note: We assume if !isCreator, I am the counterparty (based on fetch filter)
                            // So any name that is NOT 'ฉัน' (Creator) must be ME.
                            if (item.debtor !== 'ฉัน') displayDebtor = 'ฉัน';
                            if (item.creditor !== 'ฉัน') displayCreditor = 'ฉัน';
                        }

                        return (
                        <div key={item.id} style={{ backgroundColor: 'var(--color-glass-strong)', padding: '16px', borderRadius: '12px', border: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <div style={{ fontSize: '12px', color: 'var(--color-text-subtle)' }}>
                                        {new Date(item.created_at).toLocaleDateString('th-TH')}
                                    </div>
                                    {!isCreator && (
                                        <span style={{ fontSize: '10px', backgroundColor: 'var(--color-accent-soft)', color: 'var(--color-accent)', padding: '2px 6px', borderRadius: '4px' }}>
                                            โดย {creatorName}
                                        </span>
                                    )}
                                    {item.status === DEBT_STATUS.PAID && (
                                        <span style={{ fontSize: '10px', backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>
                                            เคลียร์แล้ว
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: '500', color: 'var(--color-text)' }}>
                                    {displayDebtor === 'ฉัน' ? (
                                        <span>🔴 ฉันต้องจ่าย <span style={{ fontWeight: 'bold' }}>{displayCreditor}</span></span>
                                    ) : (
                                        <span>🟢 <span style={{ fontWeight: 'bold' }}>{displayDebtor}</span> ต้องจ่ายฉัน</span>
                                    )}
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: displayDebtor === 'ฉัน' ? 'var(--color-danger)' : 'var(--color-primary)', marginTop: '4px' }}>
                                    {parseFloat(item.amount).toLocaleString()} บาท
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
                                <button 
                                    onClick={() => toggleStatus(item)}
                                    style={{ 
                                        padding: '10px 20px', 
                                        borderRadius: '25px', 
                                        border: 'none',
                                        backgroundColor: item.status === DEBT_STATUS.PAID ? 'var(--color-muted)' : 'var(--color-primary)',
                                        backgroundImage: item.status === DEBT_STATUS.PAID ? 'none' : 'var(--color-primary-gradient)',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        minWidth: '100px',
                                        fontSize: '14px',
                                        boxShadow: '0 6px 14px rgba(30, 70, 90, 0.15)'
                                    }}
                                >
                                    {item.status === DEBT_STATUS.PAID ? 'ยกเลิก' : 'เคลียร์บิล'}
                                </button>
                                <button onClick={() => onDelete(item.id)} style={{ 
                                    padding: '8px 16px', 
                                    background: 'var(--color-danger-soft)', 
                                    border: 'none', 
                                    borderRadius: '20px', 
                                    color: 'var(--color-danger)', 
                                    fontSize: '12px', 
                                    fontWeight: 'bold', 
                                    cursor: 'pointer',
                                    minWidth: '60px'
                                }}>ลบ</button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ExpenseForm;
